import React, { useState, useEffect } from 'react';

const SoleMateShowcase = () => {
  const [demoMode, setDemoMode] = useState('normal');
  const [balanceScore, setBalanceScore] = useState(95);
  const [isLiveDemo, setIsLiveDemo] = useState(false);
  const [readingCount, setReadingCount] = useState(1422);

  // Live demo simulation
  useEffect(() => {
    if (isLiveDemo) {
      const interval = setInterval(() => {
        setReadingCount(prev => prev + 1);
        if (demoMode === 'normal') {
          setBalanceScore(95 + (Math.random() - 0.5) * 5);
        }
      }, 500);
      return () => clearInterval(interval);
    }
  }, [isLiveDemo, demoMode]);

  const simulateDemo = (mode) => {
    setDemoMode(mode);
    switch (mode) {
      case 'normal':
        setBalanceScore(95);
        break;
      case 'warning':
        setBalanceScore(45);
        break;
      case 'emergency':
        setBalanceScore(15);
        setTimeout(() => setDemoMode('normal'), 3000);
        break;
    }
  };

  const getStatusColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 50) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getStatusText = (score) => {
    if (score >= 80) return 'Good Balance';
    if (score >= 50) return 'Poor Posture Warning';
    return 'FALL DETECTED';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      {/* Compact Hero Section */}
      <header className="bg-gradient-to-r from-gray-900 via-black to-gray-900 border-b border-cyan-500/30 py-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-bold mb-4 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
              SoleMate
            </h1>
            <p className="text-2xl md:text-3xl mb-3 text-cyan-300 font-semibold">
              Smart Fall Prevention System
            </p>
            <p className="text-lg md:text-xl max-w-2xl mx-auto text-gray-400 mb-8">
              48-hour hackathon prototype: ML-powered fall prediction in a smart shoe
            </p>
            
            {/* Compact Stats */}
            <div className="flex justify-center gap-12 mb-8">
              <div>
                <div className="text-3xl font-bold text-cyan-400">48hrs</div>
                <div className="text-gray-500 text-sm">Build Time</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-cyan-400">95%</div>
                <div className="text-gray-500 text-sm">Accuracy</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-cyan-400">$50</div>
                <div className="text-gray-500 text-sm">Components</div>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap justify-center gap-3">
              <a
                href="https://github.com/ARNAB814/SoleMate-Hackathon"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 border border-cyan-500 text-cyan-400 px-5 py-2.5 rounded-lg hover:bg-cyan-500/10 transition-colors text-base"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.163 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.16 22 16.42 22 12c0-5.523-4.477-10-10-10z" />
                </svg>
                <span>GitHub</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        
        {/* Live Demo Section - MOVED TO TOP */}
        <div className="mb-16">
          <div className="flex items-center justify-center gap-4 mb-8">
            <h2 className="text-3xl font-bold text-cyan-400">Interactive Demo</h2>
            <button 
              onClick={() => setIsLiveDemo(!isLiveDemo)}
              className="flex items-center space-x-2 bg-cyan-500 text-black px-6 py-3 rounded-lg hover:bg-cyan-400 transition-all font-bold shadow-lg shadow-cyan-500/30 hover:scale-105"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>{isLiveDemo ? 'Stop' : 'Start'}</span>
            </button>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-black text-green-400 p-6 rounded-xl font-mono border-2 border-green-500/50 shadow-2xl shadow-green-500/20 mb-6">
              <div className="space-y-2 text-sm">
                <div className="flex"><span className="text-cyan-400">[{new Date().toLocaleTimeString()}]</span> #{readingCount.toString().padStart(4, '0')}</div>
                <div>Accel: <span className="text-yellow-400">X:-0.011 Y:-0.036 Z:1.028</span></div>
                <div className={getStatusColor(balanceScore)}>
                  Balance: <span className="text-yellow-400">{Math.round(balanceScore)}%</span> | {getStatusText(balanceScore)}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-center gap-4 mb-6">
              <button 
                onClick={() => {simulateDemo('normal'); setIsLiveDemo(true);}}
                className="bg-green-500 hover:bg-green-400 text-black px-6 py-3 rounded-lg transition-all font-bold hover:scale-105 shadow-lg"
              >
                Normal
              </button>
              <button 
                onClick={() => simulateDemo('warning')}
                className="bg-yellow-500 hover:bg-yellow-400 text-black px-6 py-3 rounded-lg transition-all font-bold hover:scale-105 shadow-lg"
              >
                Poor Posture
              </button>
              <button 
                onClick={() => simulateDemo('emergency')}
                className="bg-red-500 hover:bg-red-400 text-white px-6 py-3 rounded-lg transition-all font-bold hover:scale-105 shadow-lg"
              >
                Fall Detected
              </button>
            </div>

            <div className="bg-gray-800 rounded-full h-8 overflow-hidden border-2 border-gray-600 shadow-lg">
              <div 
                className="bg-gradient-to-r from-green-400 via-cyan-400 to-blue-400 h-full transition-all duration-500"
                style={{ width: `${balanceScore}%` }}
              ></div>
            </div>
          </div>
        </div>
        
        {/* The Story - Friendly Introduction */}
        <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-xl p-8 mb-16 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-cyan-400 mb-6">Why We Built This</h2>
          
          <div className="space-y-4 text-gray-300 leading-relaxed text-base">
            <p>
              <span className="text-white font-semibold">Every 11 seconds</span>, a senior citizen falls in the United States. 
              Most fall detection devices only sound an alarm <span className="text-white font-semibold">after</span> someone 
              has already hit the ground—when it's too late to prevent injury.
            </p>
            
            <p>
              We asked ourselves: <span className="italic text-cyan-300">"What if we could warn someone before they fall?"</span>
            </p>
            
            <p>
              That's the idea behind SoleMate. We built a smart shoe that learns how you walk and can detect when something's off 
              with your balance. Think of it like a fitness tracker, but instead of counting steps, it's watching out for your safety.
            </p>
            
            <div className="bg-cyan-900/20 border-l-4 border-cyan-400 pl-4 py-3 mt-6">
              <p className="text-cyan-100">
                Our prototype uses simple sensors and machine learning to give people a heads-up when their balance 
                is unstable—before a fall happens. And we built it for under $50 in a 48-hour hackathon.
              </p>
            </div>
          </div>
        </div>

        {/* How It Works - Narrative Style */}
        <div className="mb-16 max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 border border-gray-700 rounded-xl p-8">
            <h2 className="text-2xl font-bold text-cyan-400 mb-8">How It Works</h2>
            
            <div className="space-y-6 text-gray-300 leading-relaxed text-base">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-cyan-500/20 border-2 border-cyan-500 flex items-center justify-center">
                    <span className="text-cyan-400 font-bold">1</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">Sensing Movement</h3>
                  <p>
                    The shoe has two types of sensors built in: a 6-axis motion sensor at the toe that tracks how your foot moves 
                    in space, and a pressure sensor at the heel that measures weight distribution. Together, they create a complete 
                    picture of your balance and walking pattern.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 border-2 border-purple-500 flex items-center justify-center">
                    <span className="text-purple-400 font-bold">2</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">Learning Your Pattern</h3>
                  <p>
                    A Raspberry Pi computer inside the shoe uses machine learning to understand your unique walking style. 
                    After watching you walk normally for just 10 seconds, it knows what "good balance" looks like for you specifically. 
                    From then on, it's constantly comparing your current movement to that baseline, checking for anything unusual 
                    10 times per second.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-amber-500/20 border-2 border-amber-500 flex items-center justify-center">
                    <span className="text-amber-400 font-bold">3</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2">Providing Alerts</h3>
                  <p>
                    When the system detects that your balance is becoming unstable—like you're leaning too far or your weight 
                    distribution is unusual—it immediately alerts you with a visual warning (LED display) and a sound (buzzer). 
                    This gives you a chance to catch yourself or grab something for support before you actually fall.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Prototype Image */}
        <div className="mb-16 max-w-4xl mx-auto">
          <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 shadow-lg shadow-cyan-500/20">
            <img 
              src="assets/images/Shoe.jpg" 
              alt="SoleMate Smart Shoe Prototype"
              className="w-full h-auto rounded-lg mb-3"
            />
            <p className="text-center text-gray-400 text-sm">
              Smart shoe with 6DOF motion sensor, pressure detection, and real-time feedback
            </p>
          </div>
        </div>
        
        {/* Tech Stack Below Image */}
        <div className="mb-16 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-cyan-400 mb-8 text-center">Technology</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 hover:border-cyan-500/50 transition-all">
              <h3 className="text-lg text-cyan-300 mb-4 font-bold">Hardware</h3>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">•</span>
                  <span>LSM6DSOX 6-axis motion sensor (10Hz)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">•</span>
                  <span>Force-sensitive pressure sensor</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">•</span>
                  <span>Raspberry Pi 4 (edge processing)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">•</span>
                  <span>LED display + audio buzzer</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-900 border border-gray-700 rounded-xl p-6 hover:border-cyan-500/50 transition-all">
              <h3 className="text-lg text-cyan-300 mb-4 font-bold">Software</h3>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">•</span>
                  <span>Machine learning for gait analysis</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">•</span>
                  <span>10-second baseline learning</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">•</span>
                  <span>Real-time sensor fusion</span>
                </li>
                <li className="flex items-start">
                  <span className="text-cyan-400 mr-2">•</span>
                  <span>95% accuracy in testing</span>
                </li>
              </ul>
            </div>
          </div>
        </div>


        {/* Team */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-cyan-400 mb-8 text-center">The Team</h2>
          <div className="grid md:grid-cols-3 gap-8">
            
            {/* Arunav Saha */}
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 hover:border-cyan-500/50 transition-all">
              <div className="text-center mb-4">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold text-black">
                  AS
                </div>
                <h3 className="text-xl font-bold text-gray-100">Arunav Saha</h3>
                <p className="text-cyan-400 font-medium">Technical Lead</p>
              </div>
              <p className="text-gray-300 text-sm text-center mb-4">
                Hardware-software integration & system architecture
              </p>
              <div className="flex justify-center space-x-4">
                <a href="https://github.com/ARNAB814" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.163 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.16 22 16.42 22 12c0-5.523-4.477-10-10-10z" />
                  </svg>
                </a>
                <a href="https://www.linkedin.com/in/arunavsaha/" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Ejaz Khan */}
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 hover:border-cyan-500/50 transition-all">
              <div className="text-center mb-4">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold text-black">
                  EK
                </div>
                <h3 className="text-xl font-bold text-gray-100">Ejaz Khan</h3>
                <p className="text-cyan-400 font-medium">ML Developer</p>
              </div>
              <p className="text-gray-300 text-sm text-center mb-4">
                Machine learning algorithms & gait analysis
              </p>
              <div className="flex justify-center space-x-4">
                <a href="mailto:ejazkhan.dev@gmail.com" className="text-cyan-400 hover:text-cyan-300">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </a>
                <a href="https://github.com/Ejaz-dev" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.163 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.16 22 16.42 22 12c0-5.523-4.477-10-10-10z" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Musayed Ahmed */}
            <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 hover:border-cyan-500/50 transition-all">
              <div className="text-center mb-4">
                <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-teal-500 rounded-full mx-auto mb-4 flex items-center justify-center text-2xl font-bold text-black">
                  MA
                </div>
                <h3 className="text-xl font-bold text-gray-100">Musayed Ahmed</h3>
                <p className="text-cyan-400 font-medium">Hardware Engineer</p>
              </div>
              <p className="text-gray-300 text-sm text-center mb-4">
                Sensor system design & physical prototype
              </p>
              <div className="flex justify-center space-x-4">
                <a href="mailto:ahmedmusayed44@gmail.com" className="text-cyan-400 hover:text-cyan-300">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </a>
                <a href="https://github.com/musayeda" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.163 6.839 9.489.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.342-3.369-1.342-.454-1.155-1.11-1.462-1.11-1.462-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.16 22 16.42 22 12c0-5.523-4.477-10-10-10z" />
                  </svg>
                </a>
                <a href="https://www.linkedin.com/in/musayed-ahmed" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Team Development Photo */}
        <div className="mb-12 max-w-3xl mx-auto">
          <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 shadow-lg shadow-cyan-500/20">
            <img 
              src="assets/images/solemate_group.jpg" 
              alt="SoleMate development team"
              className="w-full h-auto rounded-lg"
            />
            <p className="text-center text-gray-400 text-sm mt-4">
              The SoleMate team during the 48-hour hackathon
            </p>
          </div>
        </div>
      </main>
      {/* Compact Footer */}
      <footer className="bg-black text-white py-8 border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-gray-400 mb-4">
            SoleMate — 48-hour hackathon prototype exploring ML-powered fall prevention
          </p>
          <div className="flex justify-center space-x-4">
            <a 
              href="https://github.com/ARNAB814/SoleMate-Hackathon"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-cyan-500 text-cyan-400 px-6 py-2 rounded-lg hover:bg-cyan-500/10 transition-colors"
            >
              View Code
            </a>
            <a 
              href="https://github.com/ARNAB814/SoleMate-Hackathon/blob/main/documentation.md"
              target="_blank"
              rel="noopener noreferrer"
              className="border border-cyan-500 text-cyan-400 px-6 py-2 rounded-lg hover:bg-cyan-500/10 transition-colors"
            >
              Documentation
            </a>
          </div>
          <p className="text-gray-500 text-sm mt-4">© 2024 SoleMate Team</p>
        </div>
      </footer>
    </div>
  );
};

export default SoleMateShowcase;